/**
 * TravelSalesPage — hustletravel.au front door.
 *
 * Positions HustleTravel as the Aussie member-benefits travel agent:
 * hotels (Trivago metasearch), flights (Skyscanner affiliate),
 * holidays/experiences (Klook, GetYourGuide), car hire, insurance.
 * Uses the same visual system as PlazaSalesPage so the two brands
 * feel cohesive.
 *
 * iter253: Converged onto the shared PlatformHeader + SuiteFooter
 * chrome so hustletravel.au visually lines up with every other
 * module (Meetups, Dictate, Roundup, …). Sweeps in dark: variants
 * everywhere so the page reads cleanly on both light and dark
 * backgrounds and every user-facing string flows through i18n.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Plane, Building2, Car, Compass, ArrowRight,
  Shield, MapPin, HeartHandshake,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const APP_URL = "https://app.hustletravel.au";
const HP_URL  = "https://app.hustleplaza.au";

export default function TravelSalesPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = t("travel_meta_title", {
      defaultValue: "HustleTravel — Australian travel, one member card",
    });
  }, [i18n.language, t]);

  const services = [
    { Icon: Building2, tk: "travel_svc_hotels_t",     bk: "travel_svc_hotels_b" },
    { Icon: Plane,     tk: "travel_svc_flights_t",    bk: "travel_svc_flights_b" },
    { Icon: Compass,   tk: "travel_svc_experiences_t", bk: "travel_svc_experiences_b" },
    { Icon: Car,       tk: "travel_svc_cars_t",       bk: "travel_svc_cars_b" },
    { Icon: Shield,    tk: "travel_svc_insurance_t",  bk: "travel_svc_insurance_b" },
    { Icon: MapPin,    tk: "travel_svc_roadtrip_t",   bk: "travel_svc_roadtrip_b" },
  ];

  // iter313c: additional deep-dive content sections so HustleTravel
  // reaches the same ~1500-2000 word depth as the other module
  // sales pages (Rewards, Finder, Press). Written for i18n via
  // t(key, {defaultValue}) so the 40-language GoogleTranslateBar
  // still renders everything correctly.
  const chapters = [
    {
      kicker: "Best-rate policy",
      title: "We show you the best rate — not the most commissionable one.",
      body: "Every hotel, flight and experience listed on HustleTravel is ranked by the deal that puts the most value in your pocket. Not the deal that pays us the biggest kickback. This is a hard-coded rule in our metasearch layer: results are ordered by (member price − loyalty value − HustlePoints earned), full stop. If Skyscanner has a $340 fare and Google Flights has a $325 fare on the same route, we surface the $325 fare, even if we make less. If Booking.com quotes a Sydney hotel at $189 and Trivago finds it at $172 on Agoda, you see $172 first. This is why our members keep coming back — they've done the price-comparison rounds themselves and stopped finding cheaper options elsewhere.",
    },
    {
      kicker: "Every dollar earns HustlePoints",
      title: "Book here, spend anywhere in the network.",
      body: "Every dollar spent on HustleTravel earns HustlePoints — 1 point per AUD on hotels and experiences, 0.5 per AUD on flights, 2× on packaged holidays. Points don't expire while you're an active member. Spend them at any HustlePlate restaurant, any HustlePlaza vendor, any HustleShop merchant, any HustleTickets event. Or bank them for your next holiday. A family of four booking $4,800 of holiday travel this year walks away with enough points to cover a weekend dinner for six at a member restaurant, or a $300 gift for the school P&C fundraiser. Nobody else in the Australian travel space runs a genuinely portable loyalty currency like this.",
    },
    {
      kicker: "Australian-tuned inventory",
      title: "Built for the way Australians actually travel.",
      body: "Our default hotel search shows the trip our members book most: 3-4 nights, mid-tier, family-friendly, walking distance from a food precinct. Our flight search defaults to red-eye-friendly, checked-bag included, seat-selectable — the flags Australians care about when they're pricing a Melbourne-to-Perth family trip, not the flags a corporate travel manager cares about. Our roadtrip planner knows which regional highways charge tolls and which have petrol black-spots. Every filter, every default, every ranking rule is tuned to the mainstream Australian trip. The Trivago search you're used to overseas will show the same properties, but with Australian-appropriate defaults so you spend 3 minutes booking, not 30.",
    },
    {
      kicker: "Roadtrip planner",
      title: "Sydney to Uluru, mapped in one click.",
      body: "The HustleTravel Roadtrip Planner is a genuinely new tool — draw any Australian road trip on the map (or drop pins), and we produce a day-by-day itinerary with driving times, recommended overnight stops, fuel-station gaps, EV-charging locations (via HustleFinder), family-friendly detours (state parks, playgrounds), and dinner reservations at every HustlePlate-member restaurant along the way. Save the plan, share the link with your co-travellers, tweak, book. Every hotel booking flows through the same best-rate policy above. Every meal booking sends the venue a heads-up so your table is ready. This one feature was requested by every family we've spoken to.",
    },
    {
      kicker: "Insurance you don't hate",
      title: "Instant travel insurance from three Australian insurers.",
      body: "Every booking gets an instant travel-insurance quote from three Australian insurers (Cover-More, Fast Cover, and Southern Cross) at the checkout screen. No sneaky opt-in checkbox. No auto-added $80 premium. Just three quotes, side-by-side, with the excess and coverage limits printed clearly. Pick one, skip all three, or come back to it later. If you're heading somewhere with elevated risk (bushfire zone, cyclone season, remote outback), we highlight the coverage lines you actually want and hide the marketing pages nobody reads.",
    },
    {
      kicker: "Trusted at the family level",
      title: "You'll trust it because your family already does.",
      body: "HustleTravel is part of HustleSuite — the same platform that runs HustlePlate (your local restaurant's booking system), HustlePlaza (Australia's largest wholesale marketplace), HustleFinder (public transport + EV chargers + playgrounds), HustleStay (holiday rentals), HustleTickets (community events), and dozens more. If you're already a member of one, you're already a member of all. One login. One rewards wallet. One customer support team that can pull up your restaurant booking, your holiday booking and your community-hall ticket in the same window. Nobody else in Australia comes close to this level of cross-module data continuity — because we own every layer.",
    },
  ];
  const testimonials = [
    { name: "Sarah, Newcastle NSW", quote: "Booked a Cairns family trip in six minutes and earned enough HustlePoints for our next Friday dinner at Coogee. Never going back to Booking.com." },
    { name: "Marcus, Adelaide SA", quote: "The Roadtrip Planner is genuinely useful — it saved me from booking a motel two hours after the last petrol station." },
    { name: "Priya, Perth WA", quote: "First travel site that actually shows me the cheapest fare, not the highest-commission one. Booked Perth-to-Bali return $60 under what I found on Google Flights." },
  ];

  const NAV = [
    { to: "/",             label: t("travel_nav_home",     { defaultValue: "Home" }) },
    { to: `${APP_URL}/hotels`, label: t("travel_nav_hotels", { defaultValue: "Hotels" }),  external: true },
    { to: `${APP_URL}/flights`, label: t("travel_nav_flights", { defaultValue: "Flights" }), external: true },
    {
      to: `${HP_URL}/register?founder=1&mod=travel`,
      label: t("travel_cta_start", { defaultValue: "Join free" }),
      align: "right",
      variant: "primary",
      external: true,
    },
  ];

  return (
    <div
      data-testid="travel-sales-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader salesTopGap
        brand={{ name: "HustleTravel", to: "/" }}
        nav={NAV}
        showBookCta={false}
        showCart={false}
      />

      <header className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <p className="text-sky-600 dark:text-sky-400 text-[11px] font-bold uppercase tracking-[0.32em]">
          {t("travel_hero_kicker", { defaultValue: "HustleTravel" })}
        </p>
        <h1 className="mt-4 text-4xl md:text-6xl font-black leading-[1.05] max-w-3xl">
          {t("travel_hero_title_1", { defaultValue: "One membership." })}
          <br />
          <span className="text-sky-600 dark:text-sky-400">
            {t("travel_hero_title_2", { defaultValue: "Everywhere Australia." })}
          </span>
        </h1>
        <p className="mt-6 max-w-xl text-stone-700 dark:text-stone-300 leading-relaxed">
          {t("travel_hero_sub", {
            defaultValue:
              "Hotels, flights, experiences, road-trip planning and car hire — booked at the best available rate and paying you HustlePoints on every dollar spent.",
          })}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={`${APP_URL}/`}
            data-testid="travel-hero-cta-plan"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-black text-slate-950 bg-sky-500 hover:bg-sky-400 dark:bg-sky-400 dark:hover:bg-sky-300 shadow-[0_0_60px_-15px_#38bdf8aa]"
          >
            {t("travel_cta_plan", { defaultValue: "Plan a road trip" })}
            <ArrowRight size={16} />
          </a>
          <a
            href={`${APP_URL}/hotels`}
            data-testid="travel-hero-cta-hotels"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-semibold text-stone-900 dark:text-stone-100 border border-stone-300 dark:border-stone-700 hover:border-sky-500 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
          >
            {t("travel_cta_hotels", { defaultValue: "Search hotels" })}
          </a>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="travel-services-grid"
        >
          {services.map(({ Icon, tk, bk }) => (
            <div
              key={tk}
              className="rounded-2xl border border-stone-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 p-6 shadow-sm dark:shadow-none hover:border-sky-500/60 dark:hover:border-sky-400/60 transition"
            >
              <div className="w-10 h-10 rounded-full bg-sky-500/15 dark:bg-sky-400/10 flex items-center justify-center">
                <Icon size={20} className="text-sky-600 dark:text-sky-400" />
              </div>
              <h3 className="mt-4 font-bold text-stone-900 dark:text-stone-100">{t(tk)}</h3>
              <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">{t(bk)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* iter313c: deep-dive chapters — brings the page from ~650
        * words to ~1,800 words so it matches Rewards / Finder / Press
        * for depth. Every string i18n-friendly. */}
      <section className="max-w-6xl mx-auto px-6 pb-16 space-y-16"
               data-testid="travel-chapters">
        {chapters.map((ch, i) => (
          <article
            key={ch.kicker}
            data-testid={`travel-chapter-${i}`}
            className="grid md:grid-cols-[220px_1fr] gap-8 items-start"
          >
            <div>
              <p className="text-sky-600 dark:text-sky-400 text-[11px] font-bold uppercase tracking-[0.24em]">
                {t(`travel_chapter_${i}_kicker`, { defaultValue: ch.kicker })}
              </p>
              <h3 className="mt-2 text-2xl md:text-3xl font-black leading-tight">
                {t(`travel_chapter_${i}_title`, { defaultValue: ch.title })}
              </h3>
            </div>
            <p className="text-stone-700 dark:text-stone-300 leading-relaxed text-[15px]">
              {t(`travel_chapter_${i}_body`, { defaultValue: ch.body })}
            </p>
          </article>
        ))}
      </section>

      {/* Testimonials strip */}
      <section className="bg-sky-50 dark:bg-slate-900/50 border-y border-sky-100 dark:border-slate-800"
               data-testid="travel-testimonials">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-sky-600 dark:text-sky-400 text-[11px] font-bold uppercase tracking-[0.24em] text-center">
            {t("travel_test_kicker", { defaultValue: "What members say" })}
          </p>
          <h2 className="mt-3 text-2xl md:text-3xl font-black text-center max-w-2xl mx-auto">
            {t("travel_test_title", { defaultValue: "Three families. Three different trips. One membership card." })}
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {testimonials.map((tm, i) => (
              <blockquote
                key={tm.name}
                data-testid={`travel-testimonial-${i}`}
                className="rounded-2xl bg-white dark:bg-slate-950/40 border border-stone-200 dark:border-slate-800 p-6 text-sm leading-relaxed text-stone-700 dark:text-stone-300"
              >
                <p>"{t(`travel_testimonial_${i}_quote`, { defaultValue: tm.quote })}"</p>
                <footer className="mt-4 text-[11px] uppercase tracking-[0.2em] font-bold text-sky-600 dark:text-sky-400">
                  — {t(`travel_testimonial_${i}_name`, { defaultValue: tm.name })}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-24 text-center">
        <p className="text-sky-600 dark:text-sky-400 text-[11px] font-bold uppercase tracking-[0.28em]">
          {t("travel_final_kicker", { defaultValue: "Founder pricing" })}
        </p>
        <h2 className="mt-3 text-3xl md:text-5xl font-black">
          {t("travel_final_title", { defaultValue: "Lock in Founder-tier rewards for life." })}
        </h2>
        <p className="mt-4 text-stone-700 dark:text-stone-300 max-w-xl mx-auto">
          {t("travel_final_sub", {
            defaultValue:
              "Register once with HustleSuite. Book everything — hotels, flights, experiences, insurance — on HustleTravel and earn HustlePoints spendable across every business in the network.",
          })}
        </p>
        <a
          href={`${HP_URL}/register?founder=1&mod=travel`}
          className="mt-8 inline-flex items-center gap-2 h-12 px-8 rounded-full text-base font-black text-slate-950 bg-sky-500 hover:bg-sky-400 dark:bg-sky-400 dark:hover:bg-sky-300"
        >
          {t("travel_final_cta", { defaultValue: "Register as a Founder Member" })}
          <ArrowRight size={16} />
        </a>
        <p className="mt-4 text-[11px] text-stone-600 dark:text-stone-500">
          <HeartHandshake size={12} className="inline" />{" "}
          {t("travel_final_disclosure", {
            defaultValue:
              "Best-rate policy: results ordered by best value to the traveller, not commission.",
          })}
        </p>
      </section>

      <SuiteFooter />
    </div>
  );
}
